<script setup lang="tsx" generic="T = { data: { test: '' } }">
import {
  type Column,
  type ColumnDef,
  FlexRender,
  type Row,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useVueTable,
} from "@tanstack/vue-table"
import type { SortingState } from "@tanstack/vue-table"
import { merge } from "lodash-es"
import { LucideChevronDown } from "lucide-vue-next"

import type { SortByParams } from "@finq/app-base/lib/types/common"

import type { UiTableFetcherProps } from "./Fetcher.interface"
import { useSkeletonTable } from "./shared"
import { generateMetaStyles, getRowLinkValue, getTableClasses, useTableCore } from "./shared"

const props = withDefaults(defineProps<UiTableFetcherProps<T>>(), {
  pageMapper: (page: T | PaginationResponse<T>) =>
    page && typeof page === "object" && "data" in page ? page.data : page,
  loadingSkeletons: 5,
  enableSorting: true,
  useLoadMoreButton: false,
})

const { data, status, isRefetching } = props.query
const columnHelper = createColumnHelper<T>()

const sortingRaw = ref<SortingState>([]) // Table raw sorting value
const sorting = defineModel<SortByParams | undefined>("sorting", { default: () => undefined }) // v-model sorting value
const { isMobile } = useDisplay()
const slots = useSlots()
const { t } = useI18n({ useScope: "local" })

// @ts-expect-error - This is a hack to get the type of the data
// prettier-ignore
const flatData = computed(() => (data.value?.pages?.flatMap(props.pageMapper) ?? []) as T["data"] extends never ? T : T["data"])

const mappedData = computed(() => {
  if (!data.value || typeof data.value !== "object") return []

  if ("pages" in data.value) {
    return flatData.value
  }

  const [isArray, res] = toArrayResolver(data.value as any)
  const mappedRes = (res as any[]).map(props.pageMapper)

  return isArray ? mappedRes : mappedRes[0]
})

const hasNextPage = computed(() => {
  return "hasNextPage" in props.query && props.query.hasNextPage.value
})

const isFetchingNextPage = computed(() => {
  return "fetchNextPage" in props.query && props.query.isFetchingNextPage.value
})

defineExpose({ flatData, mappedData })

const classes = computed(() => getTableClasses(props.variant, props.classes))

// Syncing v-model sorting value with the table's raw sorting value
watchEffect(() => {
  if (sorting.value?.sortBy) {
    sortingRaw.value = [{ id: sorting.value.sortBy, desc: sorting.value.order === "DESC" }]
  }
})

const tableColumns = computed(() => {
  if (typeof props.columns === "function") {
    return props.columns(columnHelper) as ColumnDef<T>[]
  }

  return (props.columns || []) as ColumnDef<T>[]
})

const { skeletonTable } = useSkeletonTable({
  loadingCount: props.loadingSkeletons,
  columns: tableColumns.value,
})

const table = useVueTable({
  get data() {
    return mappedData.value as any[]
  },
  get columns() {
    return tableColumns.value
  },
  state: {
    get sorting() {
      return sortingRaw.value
    },
  },
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),

  get enableSorting() {
    return isMobile.value ? false : props.enableSorting
  },
  get manualSorting() {
    return isMobile.value ? false : props.enableSorting
  },
  enableSortingRemoval: false,

  onSortingChange(updaterOrValue) {
    const canSort = props.beforeSort?.()
    if (canSort === false) return

    const rawSort = typeof updaterOrValue === "function" ? updaterOrValue(sortingRaw.value) : updaterOrValue
    const mappedSort = rawSort.reduce(
      (acc, { id, desc }) => {
        return {
          ...acc,
          sortBy: id,
          order: (desc ? "DESC" : "ASC") as "ASC",
        }
      },
      undefined as SortByParams | undefined
    )

    sortingRaw.value = rawSort
    sorting.value = mappedSort
  },
})

const tableCore = useTableCore(table)
const { rows, hasFooter } = tableCore

const intersectionTarget = ref(null)

const fetchNextPage = () => {
  if (hasNextPage.value && "fetchNextPage" in props.query) {
    props.query.fetchNextPage()
  }
}

if (!props.useLoadMoreButton)
  useIntersectionObserver(intersectionTarget, onIntersectionObserver, { rootMargin: "200px" })

function onIntersectionObserver([{ isIntersecting }]: IntersectionObserverEntry[]) {
  if (
    !props.isInfinite ||
    !isIntersecting ||
    !hasNextPage.value ||
    status.value === "pending" ||
    isRefetching.value
  )
    return

  fetchNextPage()
}

const [DefineLoadingTemplate, ReuseLoadingTemplate] = createReusableTemplate()

const rowAttrs = computed(() => {
  if (slots["expandableRow"]) return { is: "div" }
  return { is: resolveComponent("Fragment") }
})

function generateRowDataAttributes(row: Row<T>) {
  if (slots["expandableRow"]) {
    return row.getIsExpanded() ? "expanded" : "collapsed"
  }
  return row.getIsSelected() ? "selected" : "default"
}
</script>

<template>
  <!-- Loading skeleton -->
  <DefineLoadingTemplate>
    <UiTableBody :class="classes?.body?.root">
      <UiTableRow v-for="row in skeletonTable.getRowModel().rows" :key="row.id" :class="classes?.body?.row">
        <UiTableCell
          v-for="cell in row.getVisibleCells()"
          :key="cell.id"
          :class="cn(classes?.body?.cell, cell.column.columnDef.meta?.classes?.cell)"
          :style="{ flex: cell.column.columnDef.meta?.flex, width: cell.column.columnDef.meta?.width }"
        >
          <UiSkeleton :class="cn('my-2 h-4', classes?.skeletonRow)" loading />
        </UiTableCell>
      </UiTableRow>
    </UiTableBody>
  </DefineLoadingTemplate>

  <UiTable :class="classes?.root">
    <UiTableHeader :class="classes?.header?.root">
      <UiTableRow
        v-for="headerGroup in table.getHeaderGroups()"
        :key="headerGroup.id"
        :class="classes?.header?.row"
        is="div"
      >
        <template v-for="header in headerGroup.headers" :key="header.id">
          <UiTableHead
            v-if="!header?.column.columnDef.meta?.hiddenHeader"
            :class="cn('group', classes?.header?.cell, header.column.columnDef.meta?.classes?.headerCell)"
            :style="generateMetaStyles(header.column)"
            :is="!header.column.columnDef.enableSorting ? 'div' : undefined"
            @click="
              header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler()?.($event) : null
            "
          >
            <span class="relative">
              <FlexRender
                v-if="!header.isPlaceholder"
                :render="header.column.columnDef.header"
                :props="header.getContext()"
              />

              <LucideChevronDown
                v-if="header.column.columnDef.enableSorting"
                :size="12"
                :class="
                  cn(
                    'absolute -end-4 top-1/2 -translate-y-1/2 opacity-0 transition-colors transition-opacity group-hover:opacity-100',
                    {
                      'text-primary opacity-100': header.column.getIsSorted(),
                      'rotate-180': header.column.getIsSorted() && sorting?.order === 'DESC',
                    }
                  )
                "
              />
            </span>
          </UiTableHead>
        </template>
      </UiTableRow>
    </UiTableHeader>

    <UiTransition leave-absolute appear>
      <ReuseLoadingTemplate v-if="status === 'pending' || isRefetching" />

      <UiTableBody v-else-if="status === 'error' || !rows.length" :class="classes?.body?.root">
        <UiTableRow class="border-none">
          <UiTableCell class="flex-1 p-0">
            <UiNoData :config="noDataConfig" gray-card :classes="classes?.noData" />
          </UiTableCell>
        </UiTableRow>
      </UiTableBody>

      <UiTableBody v-else-if="status === 'success' && rows?.length" :class="cn(classes?.body?.root)">
        <component
          v-for="(row, index) in rows"
          :class="cn(classes?.body?.expandable?.root)"
          :is="rowAttrs.is"
          :key="row.id"
        >
          <UiTableRow
            :class="
              cn(
                classes?.body?.row,
                rowAttrs.is === 'div' && index === rows.length - 1 ? 'border-none' : '',
                locked && index > 4 ? 'pointer-events-none select-none blur' : ''
              )
            "
            :data-index="row.index"
            :data-state="generateRowDataAttributes(row)"
            :to="getRowLinkValue(row, rowLink)"
            :onClick="onRowClick ? () => onRowClick?.(row) : undefined"
            :onMouseEnter="onRowMouseEnter ? () => onRowMouseEnter?.(row) : undefined"
          >
            <UiTableCell
              v-for="cell in row.getVisibleCells()"
              :key="cell.id"
              :class="cn(classes?.body?.cell, cell.column.columnDef.meta?.classes?.cell)"
              :style="generateMetaStyles(cell.column)"
            >
              <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
            </UiTableCell>
          </UiTableRow>

          <div
            v-auto-animate
            v-if="slots['expandableRow']"
            :class="cn('pointer-events-auto w-full cursor-auto', classes?.body?.expandable?.row)"
          >
            <slot v-bind="{ row }" v-if="row.getIsExpanded()" name="expandableRow" />
          </div>
        </component>
      </UiTableBody>
    </UiTransition>

    <UiTableFooter v-if="hasFooter" :class="cn(classes?.footer?.root)">
      <UiTableRow
        v-for="footerGroup in table.getFooterGroups()"
        :key="footerGroup.id"
        :class="cn(classes?.footer?.row)"
      >
        <UiTableCell
          v-for="footer in footerGroup.headers"
          :key="footer.id"
          :class="cn(classes?.footer?.cell, footer.column.columnDef.meta?.classes?.cell)"
          :style="generateMetaStyles(footer.column)"
        >
          <FlexRender :render="footer.column.columnDef.footer" :props="footer.getContext()" />
        </UiTableCell>
      </UiTableRow>
    </UiTableFooter>

    <ReuseLoadingTemplate v-if="isFetchingNextPage" />
  </UiTable>

  <!-- Intersection fetcher -->
  <div
    class="invisible"
    v-if="!props.useLoadMoreButton && isInfinite && hasNextPage"
    ref="intersectionTarget"
  ></div>

  <!-- Load more button -->
  <div class="my-4 text-center" v-if="props.useLoadMoreButton && hasNextPage && !locked">
    <UiButton
      class="gap-1 bg-transparent text-black/50 hover:bg-transparent"
      @click="fetchNextPage"
      :disabled="isFetchingNextPage"
    >
      <span>{{ t("load_all") }}</span>

      <LucideChevronDown :size="16" />
    </UiButton>
  </div>
</template>

<i18n lang="json">
{
  "he": {
    "load_all": "תראו לי הכל"
  },
  "en": {
    "load_all": "Show Me Everything"
  }
}
</i18n>
